import React from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import { faBars, faBell, faPlus, faSparkles, faSquareCheck } from '@fortawesome/pro-regular-svg-icons';

import { Button } from 'components/ui/button';
import OmniSearch from 'components/omni_search';
import { openAssistant } from 'components/ai/assistant/events';

import ProfileMenu from './profile_menu';
import type { OsNavItem, OsNavProfileItem, OsNavTopBar } from './types';

interface ActionProps {
  label: string;
  icon: IconDefinition;
  href?: string;
  hasUnread?: boolean;
}

function Action({ label, icon, href, hasUnread }: ActionProps) {
  return (
    <span className="os-nav__action-wrap d-none d-md-inline-flex">
      <Button
        aria-label={label}
        className="os-nav__action"
        disabled={!href}
        href={href}
        icon={icon}
        variant="ghost"
      />
      {hasUnread && <span className="os-nav__dot" />}
    </span>
  );
}

interface TopBarProps {
  topBar: OsNavTopBar;
  isMobile: boolean;
  isMobileOpen: boolean;
  tasks?: OsNavItem;
  profileMenu: OsNavProfileItem[];
  isExpanded: boolean;
  isProfileOpen: boolean;
  onToggle: () => void;
  onProfileOpenChange: (open: boolean) => void;
}

export default function TopBar({
  topBar,
  isMobile,
  isMobileOpen,
  tasks,
  profileMenu,
  isExpanded,
  isProfileOpen,
  onToggle,
  onProfileOpenChange,
}: TopBarProps) {
  const { homePath, logo, aiAssistant, recordSearch, user } = topBar;

  return (
    <nav className="os-nav__topbar">
      <div className="os-nav__topbar-start">
        <Button
          aria-controls={isMobile ? 'os-nav-menu' : 'os-nav-rail'}
          aria-expanded={isMobile ? isMobileOpen : isExpanded}
          aria-label={(isMobile ? isMobileOpen : isExpanded) ? 'Close navigation' : 'Open navigation'}
          className="os-nav__toggle"
          icon={faBars}
          variant="ghost"
          onClick={onToggle}
        />

        <a className="os-nav__brand d-none d-md-block" href={homePath}>
          <img alt="Buildout" className={logo.class} src={logo.src} />
        </a>
      </div>

      <div className="os-nav__topbar-center">
        {(aiAssistant || recordSearch) && (
          <OmniSearch aiEnabled={aiAssistant} recordSearchEnabled={recordSearch} />
        )}

        {aiAssistant && (
          <Button
            aria-label="Assistant"
            className="os-nav__assistant"
            iconEnd={faSparkles}
            variant="secondary"
            onClick={() => openAssistant()}
          >
            <span className="d-none d-md-inline">Assistant</span>
          </Button>
        )}
      </div>

      <div className="os-nav__topbar-end">
        <Action icon={faPlus} label="New" />

        <Action
          hasUnread={tasks?.hasUnread}
          href={tasks?.path}
          icon={faSquareCheck}
          label="Tasks"
        />

        <Action icon={faBell} label="Notifications" />

        <ProfileMenu
          isOpen={isProfileOpen}
          items={profileMenu}
          user={user}
          onOpenChange={onProfileOpenChange}
        />
      </div>
    </nav>
  );
}
